import { ChipMetadata, Button, Modal } from '@pmi/dsm-react';
import { CircleRemoveIcon } from '@pmi/dsm-react/dist/components/icons/16';
import { useSelector } from 'react-redux';
import { useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import styles from './activeAlertsList.module.scss';
import {
  useGetCustomAccountAlertsQuery,
  useRemoveCustomAlertMutation,
} from './apiSlice';
import { accountSelector } from '../../../shared/store/shared/accountSlice';
import { CustomAlertResponse } from './models';
import { toastWithAnalytics } from '../../../shared/analytics';
import { AlertsSortOrder } from '../../../shared/components/AlertGroup/models';
import { groupBy } from '../../../shared/helpers';
import AccountCustomAlert from '../../../shared/components/AlertGroup/Categories/AccountCustomAlert';

function AlertGroup({
  alerts,
  title,
  removeAlert,
  removeStatus,
}: {
  alerts: CustomAlertResponse[];
  title: string;
  removeAlert: (alert: CustomAlertResponse) => void;
  removeStatus: boolean;
}) {
  return (
    <div className="d-flex flex-column mt-4">
      <div>
        <ChipMetadata text={title} />
      </div>
      {alerts.map((alert) => (
        <div className="mt-2">
          <div className="row align-items-center">
            <div className="col">
              <AccountCustomAlert message={alert} />
            </div>
            <div className="col-auto">
              <Button
                variant="light"
                titleText="Remove alert"
                icon={CircleRemoveIcon}
                onClick={() => removeAlert(alert)}
                disabled={removeStatus}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ActiveAlertsList() {
  const { currentAccountId } = useSelector(accountSelector);
  const [removeCustomAlert, removeCustomAlertStatus] =
    useRemoveCustomAlertMutation();
  const {
    data: alerts,
    isLoading,
    isFetching,
  } = useGetCustomAccountAlertsQuery(currentAccountId, {
    skip: !currentAccountId,
  });
  const [confirmationModalState, setConfirmationModalState] = useState(false);
  const [currentAlert, setCurrentAlert] = useState<CustomAlertResponse>();

  const groupedAlerts = useMemo(
    () =>
      groupBy(alerts ?? [], (alert) =>
        alert.global ? 'global' : 'thisAccount',
      ),
    [alerts],
  );

  const showConfirmationModal = (alert: CustomAlertResponse) => {
    setCurrentAlert(alert);
    setConfirmationModalState(true);
  };

  const hideConfirmationModal = () => {
    setConfirmationModalState(false);
    setCurrentAlert(null);
  };

  const onRemoveCustomAlert = () => {
    removeCustomAlert({
      accountId: currentAccountId,
      alertId: currentAlert.id,
    })
      .unwrap()
      .then(() => {
        toastWithAnalytics('Alert message removed successfully', 'success');
        hideConfirmationModal();
      })
      .catch(() => {
        toastWithAnalytics('Error while removing alert message', 'danger');
      });
  };

  if (isLoading || isFetching)
    return (
      <div className="d-flex justify-content-center">
        <Spinner />
      </div>
    );

  if (!alerts?.length)
    return (
      <div
        className={`d-flex justify-content-center ${styles.alertListContainer}`}
      >
        <h6>There are no active alerts.</h6>
      </div>
    );

  return (
    <>
      <div className={`flex flex-column ${styles.alertListContainer}`}>
        <h6>Active Alerts</h6>
        {groupedAlerts.get('global')?.length && (
          <AlertGroup
            alerts={groupedAlerts
              .get('global')
              .sort(
                (a, b) => AlertsSortOrder[a.type] - AlertsSortOrder[b.type],
              )}
            title="Global"
            removeAlert={showConfirmationModal}
            removeStatus={removeCustomAlertStatus.isLoading}
          />
        )}
        {groupedAlerts.get('thisAccount')?.length && (
          <AlertGroup
            alerts={groupedAlerts
              .get('thisAccount')
              .sort(
                (a, b) => AlertsSortOrder[a.type] - AlertsSortOrder[b.type],
              )}
            title="This Account"
            removeAlert={showConfirmationModal}
            removeStatus={removeCustomAlertStatus.isLoading}
          />
        )}
      </div>
      <Modal
        variant="dialog"
        headline="Remove Alert"
        description={
          currentAlert?.global
            ? 'Note: removing this alert will remove it from all accounts.'
            : null
        }
        headline_description="Are you sure you want to resolve this alert and remove it? "
        buttons={[
          {
            titleText: 'Cancel',
            variant: 'outline-secondary',
            onClick: hideConfirmationModal,
            disabled: removeCustomAlertStatus.isLoading,
          },
          {
            titleText: 'Remove',
            variant: 'primary',
            disabled: removeCustomAlertStatus.isLoading,
            onClick: onRemoveCustomAlert,
          },
        ]}
        onClose={hideConfirmationModal}
        show={confirmationModalState}
      />
    </>
  );
}
